import React from 'react';
import styled from 'styled-components';
import { formatters } from '../services/api';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  LuChartColumn,
  LuClock,
  LuTriangleAlert,
  LuCalendar,
  LuDollarSign
} from 'react-icons/lu';

const SummaryContainer = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const SummaryTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;



const ProgressSection = styled.div`
  margin-top: 24px;
`;

const ProgressTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.colors.success};
  border-radius: 6px;
  transition: width 0.3s ease;
  width: ${props => props.percentage}%;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: ${props => props.theme.colors.grayMedium};
`;

const StatusSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 20px;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 8px;
  border-left: 4px solid ${props => props.color};
`;

const StatusIcon = styled.div`
  font-size: 20px;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
`;

const StatusLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ChartContainer = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
`;

const ChartTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 16px;
  text-align: center;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  border-left: 4px solid ${props => props.color || props.theme.colors.primary};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const MetricValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color || props.theme.colors.primary};
  margin-bottom: 4px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 500;
`;

const NextPaymentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;

const NextPaymentItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.secondaryBlue};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const NextPaymentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.colors.grayDark};

  svg {
    color: ${props => props.color || props.theme.colors.grayMedium};
  }

  strong {
    font-weight: 600;
  }
`;

const AttentionAlert = styled.div`
  margin-top: 24px;
  padding: 20px;
  background: #fff1f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #991b1b;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.05);

  .icon {
    background: #ef4444;
    color: white;
    padding: 8px;
    border-radius: 10px;
    display: flex;
    flex-shrink: 0;
  }

  .content {
    flex: 1;
    font-size: 14px;
    line-height: 1.5;
    
    strong {
      display: block;
      font-size: 16px;
      margin-bottom: 4px;
    }
  }
`;

const FinancialSummary = ({ summary, payments = [] }) => {
  const {
    total,
    paid,
    pending,
    overdue,
    totalValue,
    paidValue,
    pendingValue,
    overdueValue
  } = summary;

  // Processar dados históricos para o gráfico temporal
  const processHistoricalData = () => {
    // Parser robusto para ISO, Date e 'DD/MM/AAAA'
    const parseDate = (value) => {
      if (!value) return null;
      if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
      if (typeof value === 'string') {
        // Se vier como 'DD/MM/AAAA'
        const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (ddmmyyyy) {
          const day = parseInt(ddmmyyyy[1], 10);
          const month = parseInt(ddmmyyyy[2], 10) - 1;
          const year = parseInt(ddmmyyyy[3], 10);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        }
        // ISO ou outros formatos reconhecidos pelo Date
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };
    // Mapa de nomes PT-BR
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const monthNamesMap = {
      'janeiro': 'Janeiro', 'fevereiro': 'Fevereiro', 'março': 'Março', 'abril': 'Abril',
      'maio': 'Maio', 'junho': 'Junho', 'julho': 'Julho', 'agosto': 'Agosto',
      'setembro': 'Setembro', 'outubro': 'Outubro', 'novembro': 'Novembro', 'dezembro': 'Dezembro'
    };

    // Janela fixa: mês atual voltando 5 (total 6 meses)
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // fim do mês atual
    end.setHours(23, 59, 59, 999);

    // Inicializar todos os 6 meses com zero
    const monthlyData = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[key] = {
        periodo: `${monthNames[d.getMonth()]}/${d.getFullYear()}`,
        pagos: 0,
        pendentes: 0,
        atrasados: 0,
        valorPago: 0,
        valorPendente: 0,
        valorAtrasado: 0
      };
    }

    // Agregar pagamentos dentro da janela
    (payments || []).forEach((p) => {
      const dateRef = p.DATA_BAIXA || (p.pagamento && p.pagamento !== '-' ? p.pagamento : null) || p.DTVENCIMENTO || p.vencimento || p.DATA;
      const paymentDate = parseDate(dateRef);
      const vencimentoDate = parseDate(p.DTVENCIMENTO || p.vencimento || p.DATA);
      if (!paymentDate || !vencimentoDate) return;

      if (paymentDate < start || paymentDate > end) return; // ignora meses fora da janela (futuros ou muito antigos)

      const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
      // Garantir nome do período (usa janela predefinida)
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          periodo: `${monthNamesMap[p.NOMEMES?.toLowerCase?.()] || monthNames[paymentDate.getMonth()]}/${paymentDate.getFullYear()}`,
          pagos: 0, pendentes: 0, atrasados: 0,
          valorPago: 0, valorPendente: 0, valorAtrasado: 0
        };
      }

      const valor = typeof p.valor === 'number' ? p.valor : (p.VALOR || 0) / 100;
      const isPaid = p.STATUS_BOL === 1 || !!p.DATA_BAIXA || p.status === 'Pago' || (p.pagamento && p.pagamento !== '-');
      const isOverdue = !isPaid && vencimentoDate < now;

      if (isPaid) {
        monthlyData[monthKey].pagos += 1;
        monthlyData[monthKey].valorPago += valor;
      } else if (isOverdue) {
        monthlyData[monthKey].atrasados += 1;
        monthlyData[monthKey].valorAtrasado += valor;
      } else {
        monthlyData[monthKey].pendentes += 1;
        monthlyData[monthKey].valorPendente += valor;
      }
    });

    // Ordenar cronologicamente conforme a janela
    const ordered = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthlyData[key]) ordered.push(monthlyData[key]);
    }

    return ordered;
  };

  const historicalData = processHistoricalData();

  const paidPercentage = total > 0 ? Math.round((paid / total) * 100) : 0;
  const valuePaidPercentage = totalValue > 0 ? Math.round((paidValue / totalValue) * 100) : 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#10b981'; // success green
      case 'pending':
        return '#f59e0b'; // warning yellow
      case 'overdue':
        return '#ef4444'; // error red
      default:
        return '#64748b'; // gray
    }
  };

  const parseVencimento = (value) => {
    if (!value) return null;
    if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return new Date(value.split('/').reverse().join('-'));
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingPayments = (payments || [])
    .filter((p) => {
      const pago = !!p.DATA_BAIXA || p.status === 'Pago' || (p.pagamento && p.pagamento !== '-');
      if (pago) return false;
      const venc = parseVencimento(p.DTVENCIMENTO || p.vencimento || p.DATA);
      if (!venc) return false;
      venc.setHours(0, 0, 0, 0);
      return venc >= today;
    })
    .sort((a, b) => {
      const pa = parseVencimento(a.DTVENCIMENTO || a.vencimento || a.DATA);
      const pb = parseVencimento(b.DTVENCIMENTO || b.vencimento || b.DATA);
      return (pa?.getTime() || 0) - (pb?.getTime() || 0);
    })
    .slice(0, 3);

  const hasUpcomingPayments = upcomingPayments.length > 0;

  if (!hasUpcomingPayments && overdue <= 0) {
    return null;
  }

  return (
    <SummaryContainer>
      <SummaryTitle>
        Resumo Financeiro
      </SummaryTitle>

      {/* <MetricsGrid>
        <MetricCard color="#10b981">
          <MetricValue>{total}</MetricValue>
          <MetricLabel>Total de Pagamentos</MetricLabel>
        </MetricCard>
        
        <MetricCard color="#3b82f6">
          <MetricValue>{formatters.formatCurrency(totalValue)}</MetricValue>
          <MetricLabel>Valor Total</MetricLabel>
        </MetricCard>
        
        <MetricCard color="#10b981">
          <MetricValue>{paid}</MetricValue>
          <MetricLabel>Pagamentos Realizados</MetricLabel>
        </MetricCard>
        
        <MetricCard color="#f59e0b">
          <MetricValue>{pending}</MetricValue>
          <MetricLabel>Pagamentos a Vencer</MetricLabel>
        </MetricCard>
        
        {overdue > 0 && (
          <MetricCard color="#ef4444">
            <MetricValue color="#ef4444">{overdue}</MetricValue>
            <MetricLabel>Pagamentos em Atraso</MetricLabel>
          </MetricCard>
        )}
      </MetricsGrid> */}

      {/* <ChartSection>
        <ChartContainer>
          <ChartTitle>📊 Distribuição de Pagamentos</ChartTitle>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Pagos', value: paid, color: '#10b981' },
                  { name: 'A Vencer', value: pending, color: '#f59e0b' },
                  ...(overdue > 0 ? [{ name: 'Em Atraso', value: overdue, color: '#ef4444' }] : [])
                ]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {[
                  { name: 'Pagos', value: paid, color: '#10b981' },
                  { name: 'A Vencer', value: pending, color: '#f59e0b' },
                  ...(overdue > 0 ? [{ name: 'Em Atraso', value: overdue, color: '#ef4444' }] : [])
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => {
                  const totals = {
                    'Pagos': paidValue,
                    'A Vencer': pendingValue,
                    'Em Atraso': overdueValue
                  };
                  const totalStr = formatters.formatCurrency(totals[name] || 0);
                  return [`${value} título(s) — ${totalStr}`, name];
                }}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value) => value}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer>
          <ChartTitle>💰 Distribuição de Valores</ChartTitle>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={[
                { 
                  category: 'Pagos', 
                  valor: paidValue,
                  quantidade: paid,
                  color: '#10b981'
                },
                { 
                  category: 'A Vencer', 
                  valor: pendingValue,
                  quantidade: pending,
                  color: '#f59e0b'
                },
                ...(overdue > 0 ? [{
                  category: 'Em Atraso', 
                  valor: overdueValue,
                  quantidade: overdue,
                  color: '#ef4444'
                }] : [])
              ]}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="category" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickFormatter={(value) => formatters.formatCurrency(value)}
              />
              <Tooltip 
                formatter={(value, name) => [
                  formatters.formatCurrency(value), 
                  'Valor'
                ]}
                labelFormatter={(label) => `Status: ${label}`}
                contentStyle={{ 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Bar 
                dataKey="valor" 
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              >
                {[
                  { color: '#10b981' },
                  { color: '#f59e0b' },
                  ...(overdue > 0 ? [{ color: '#ef4444' }] : [])
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </ChartSection> */}


      {/* Histórico de valores por mês (substitui a seção de detalhes de datas) */}
      {/* {historicalData.length > 0 && (
        <ChartContainer style={{ marginBottom: '24px' }}>
          <ChartTitle>📊 Histórico de Valores por Mês</ChartTitle>
          {!historicalData.some(d => (d.valorPago || 0) + (d.valorPendente || 0) + (d.valorAtrasado || 0) > 0) ? (
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 16,
              textAlign: 'center',
              color: '#6b7280'
            }}>
              Nenhum valor registrado nos últimos meses para exibir no gráfico.
            </div>
          ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={historicalData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="periodo" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickFormatter={(value) => formatters.formatCurrency(value)}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const labels = {
                    valorPago: 'Valor Pago',
                    valorPendente: 'Valor a Vencer',
                    valorAtrasado: 'Valor em Atraso'
                  };
                  return [formatters.formatCurrency(value), labels[name] || name];
                }}
                labelFormatter={(label) => `Período: ${label}`}
                contentStyle={{ 
                  backgroundColor: '#f9fafb', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend 
                formatter={(value) => {
                  const labels = {
                    valorPago: 'Valor Pago',
                    valorPendente: 'Valor a Vencer',
                    valorAtrasado: 'Valor em Atraso'
                  };
                  return labels[value] || value;
                }}
              />
              <Bar dataKey="valorPago" stackId="a" fill="#10b981" />
              <Bar dataKey="valorPendente" stackId="a" fill="#f59e0b" />
              {historicalData.some(d => d.valorAtrasado > 0) && (
                <Bar dataKey="valorAtrasado" stackId="a" fill="#ef4444" />
              )}
            </BarChart>
          </ResponsiveContainer>
          )}
        </ChartContainer>
      )} */}

      {/* Próximos pagamentos a vencer (lista curta) */}
      {hasUpcomingPayments && (
        <ChartContainer style={{ marginBottom: '24px' }}>
          <ChartTitle><LuClock size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} /> Próximos Pagamentos a Vencer</ChartTitle>
          <NextPaymentsList>
            {upcomingPayments.map((p, idx) => {
              const vencRaw = p.DTVENCIMENTO || p.vencimento || p.DATA;
              const vencStr = typeof vencRaw === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(vencRaw)
                ? vencRaw
                : new Date(vencRaw).toLocaleDateString('pt-BR');
              const valor = typeof p.valor === 'number' ? p.valor : (p.VALOR || 0) / 100;
              return (
                <NextPaymentItem key={idx}>
                  <NextPaymentRow color="#3b82f6">
                    <LuCalendar size={16} />
                    <span>Vencimento: <strong>{vencStr}</strong></span>
                  </NextPaymentRow>
                  <NextPaymentRow color="#10b981">
                    <LuDollarSign size={16} />
                    <span>Valor: <strong>{formatters.formatCurrency(valor)}</strong></span>
                  </NextPaymentRow>
                </NextPaymentItem>
              );
            })}
          </NextPaymentsList>
        </ChartContainer>
      )}

      {/* <ProgressSection>
        <ProgressTitle>Progresso de Pagamentos</ProgressTitle>
        
        <div style={{ marginBottom: '20px' }}>
          <ProgressInfo>
            <span>Pagamentos Realizados</span>
            <span>{paid} de {total} ({paidPercentage}%)</span>
          </ProgressInfo>
          <ProgressBar>
            <ProgressFill percentage={paidPercentage} />
          </ProgressBar>
        </div>

        <div>
          <ProgressInfo>
            <span>Valor Pago</span>
            <span>{formatters.formatCurrency(paidValue)} de {formatters.formatCurrency(totalValue)} ({valuePaidPercentage}%)</span>
          </ProgressInfo>
          <ProgressBar>
            <ProgressFill percentage={valuePaidPercentage} />
          </ProgressBar>
        </div>
      </ProgressSection> */}

      {/* <StatusSection>
        <StatusItem color={getStatusColor('paid')}>
          <StatusIcon>✅</StatusIcon>
          <StatusInfo>
            <StatusValue>{paid}</StatusValue>
            <StatusLabel>Pagamentos Realizados</StatusLabel>
          </StatusInfo>
        </StatusItem>

        <StatusItem color={getStatusColor('pending')}>
          <StatusIcon>⏰</StatusIcon>
          <StatusInfo>
            <StatusValue>{pending}</StatusValue>
            <StatusLabel>Pagamentos a Vencer</StatusLabel>
          </StatusInfo>
        </StatusItem>

        {overdue > 0 && (
          <StatusItem color={getStatusColor('overdue')}>
            <StatusIcon>⚠️</StatusIcon>
            <StatusInfo>
              <StatusValue>{overdue}</StatusValue>
              <StatusLabel>Pagamentos em Atraso</StatusLabel>
            </StatusInfo>
          </StatusItem>
        )}
      </StatusSection> */}

      {overdue > 0 && (
        <AttentionAlert>
          <div className="icon">
            <LuTriangleAlert size={20} />
          </div>
          <div className="content">
            <strong>Atenção ao seus Débitos</strong>
            Você possui {overdue} pagamento(s) em atraso,
            totalizando {formatters.formatCurrency(overdueValue)}.
            Entre em contato conosco para regularizar sua situação o quanto antes.
          </div>
        </AttentionAlert>
      )}
    </SummaryContainer>
  );
};

export default FinancialSummary;
